import type { AdminGymCreated, AdminGymListItem } from '@steado/admin-contract/gyms';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronRight, PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { StatCards } from '@/components/panel/panels';
import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar } from '@/components/table/TableToolbar';
import { BadgeCell, PersonCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDate, formatRelative } from '@/lib/format';
import { ownerInviteBadge } from '@/lib/gym-owner-status';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { featureSwitchesQueryOptions } from '@/screens/feature-switches/feature-switches-query';

import { GymDeletionRequestsPanel } from './GymDeletionRequestsPanel';
import { NewGymDialog } from './NewGymDialog';
import { GYMS_PAGE_SIZE, gymsQueryOptions, type GymsFilters } from './gyms-query';
import { GYM_SCOPES, filtersForScope, scopeForFilters, type GymScopeId } from './gyms-scopes';

const COLUMNS: readonly TableColumn[] = [
  { key: 'gym', label: 'Gym', width: '1.6fr' },
  { key: 'status', label: 'Status', width: '1.1fr' },
  { key: 'members', label: 'Members', width: '0.9fr', align: 'right' },
  { key: 'trainers', label: 'Trainers', width: '0.8fr', align: 'right' },
  { key: 'requests', label: 'Join requests', width: '0.8fr', align: 'right' },
  { key: 'plans', label: 'Plans sent 7d', width: '0.8fr', align: 'right' },
  { key: 'signed', label: 'Created', width: '0.9fr' },
  { key: 'open', label: '', width: '44px', align: 'right' },
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || '??';
}

export interface GymsScreenProps {
  readonly filters: GymsFilters;
  readonly onFiltersChange: (next: GymsFilters) => void;
  readonly onOpenGym: (gymId: string) => void;
}

/**
 * Every gym on Steado for Gyms: who owns it, how many members it has against
 * its limit, and what it serves. Counts only; the members themselves are on
 * Users, filtered by gym, one click from each gym's page. Owners cannot sign
 * themselves up, so an admin creates each gym here and the owner is emailed
 * an invite.
 */
export function GymsScreen({ filters, onFiltersChange, onOpenGym }: GymsScreenProps) {
  const { api, can } = useAuth();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<AdminGymCreated | null>(null);

  const query = useQuery(gymsQueryOptions(api, filters));
  const counts = useQuery(scopeCountsQueryOptions(api));
  const switches = useQuery(featureSwitchesQueryOptions(api));
  const gymApiOff = switches.data?.find((item) => item.key === 'gym_api')?.enabled === false;

  const setFilter = useCallback(
    (patch: Partial<GymsFilters>) => {
      setCursorStack([]);
      onFiltersChange({ ...filters, ...patch, cursor: undefined });
    },
    [filters, onFiltersChange],
  );

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, filters.cursor]);
    onFiltersChange({ ...filters, cursor: next });
  }, [query.data, filters, onFiltersChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onFiltersChange({ ...filters, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [filters, onFiltersChange]);

  const onReset = useCallback(() => {
    setCursorStack([]);
    onFiltersChange(filtersForScope(scopeForFilters(filters), {}));
  }, [filters, onFiltersChange]);

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((gym: AdminGymListItem) => ({
        id: gym.id,
        onOpen: () => {
          onOpenGym(gym.id);
        },
        cells: [
          <PersonCell
            key="gym"
            name={gym.name}
            sub={`${gym.owner.displayName} · ${gym.owner.email}`}
            initials={initialsOf(gym.name)}
          />,
          <span key="status" className="flex min-w-0 flex-wrap items-center gap-1.5">
            {gym.memberCount >= gym.memberLimit ? <BadgeCell label="at member limit" tone="warn" /> : null}
            {gym.setupStep === 'done' ? null : <BadgeCell label="setup unfinished" tone="warn" />}
            <OwnerBadge owner={gym.owner} />
            {gym.aiNearLimit ? <BadgeCell label="AI near limit" tone="warn" /> : null}
          </span>,
          <TextCell
            key="members"
            value={`${String(gym.memberCount)}/${String(gym.memberLimit)}`}
            sub={`${String(gym.counts.loggedInWindow)} logged in 7d`}
            mono
            align="right"
          />,
          <TextCell
            key="trainers"
            value={String(gym.counts.trainersActive)}
            sub={gym.counts.trainersInvited === 0 ? null : `+${String(gym.counts.trainersInvited)} invited`}
            mono
            align="right"
          />,
          <TextCell
            key="requests"
            value={String(gym.counts.joinRequestsWaiting)}
            mono
            align="right"
            {...(gym.counts.joinRequestsWaiting > 0 ? ({ tone: 'warn' } as const) : {})}
          />,
          <TextCell
            key="plans"
            value={String(gym.counts.plansPublishedInWindow)}
            sub={`${String(gym.counts.plansPublished)} in all`}
            mono
            align="right"
          />,
          <TextCell key="signed" value={formatRelative(gym.createdAt)} sub={formatDate(gym.createdAt)} />,
          <span key="open" className="flex justify-end">
            <ChevronRight aria-hidden="true" className="size-4 text-input" />
          </span>,
        ],
      })),
    [query.data, onOpenGym],
  );

  const scope = scopeForFilters(filters);
  const isFiltered = filters.search !== undefined;
  const pageOffset = cursorStack.length * GYMS_PAGE_SIZE;
  const totals = counts.data?.gyms;

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load gyms"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const newButton = can.mutate ? (
    <Button
      type="button"
      size="sm"
      onClick={() => {
        setCreating(true);
      }}
    >
      <PlusIcon aria-hidden="true" className="size-3.5" />
      New gym
    </Button>
  ) : null;

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      {created === null ? null : (
        <Alert tone="info" role="status">
          <AlertDescription>
            <span>
              Invite sent to <span className="font-medium">{created.ownerEmail}</span>. The link works until{' '}
              {formatDate(created.inviteExpiresAt)}.
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => {
                  onOpenGym(created.gymId);
                }}
              >
                Open the gym
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => {
                  setCreated(null);
                }}
              >
                Dismiss
              </Button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      {gymApiOff ? (
        <Alert tone="warn">
          <AlertDescription>
            Steado for Gyms is switched off, so owners, trainers and members can’t reach it. This is what the
            gym tables hold.{' '}
            <Link to="/feature-switches" className="font-medium underline underline-offset-2">
              Feature switches
            </Link>
          </AlertDescription>
        </Alert>
      ) : null}

      <GymDeletionRequestsPanel />

      <StatCards
        stats={[
          { label: 'Gyms', value: totals === undefined ? '–' : String(totals.all) },
          { label: 'People coached now', value: totals === undefined ? '–' : String(totals.coachedNow) },
          { label: 'Trainers active', value: totals === undefined ? '–' : String(totals.trainersActive) },
        ]}
      />

      <ScopePills
        label="Gym scope"
        scopes={GYM_SCOPES.map((item) => ({
          id: item.id,
          label: item.label,
          ...(totals === undefined ? {} : { count: totals[item.countKey] }),
        }))}
        value={scope}
        onChange={(next: GymScopeId) => {
          setCursorStack([]);
          onFiltersChange(filtersForScope(next, filters));
        }}
      />

      <TableToolbar
        searchValue={filters.search ?? ''}
        searchPlaceholder="Gym, owner or email"
        onSearchChange={(search) => {
          setFilter({ search: search === '' ? undefined : search });
        }}
        filters={[]}
        isFiltered={isFiltered}
        onReset={onReset}
        {...(newButton === null ? {} : { action: newButton })}
      />

      <DataTable
        caption="Gyms"
        title={GYM_SCOPES.find((candidate) => candidate.id === scope)?.label ?? 'Everyone'}
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={query.isPending}
        emptyTitle={isFiltered ? 'No matches' : 'No gyms here'}
        emptyBody={
          isFiltered
            ? 'No gym, owner or email matches the search. Clear it to see every gym in this scope.'
            : 'A gym appears here once it is created, before its owner has accepted the invite.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" size="sm" onClick={onReset}>
              Clear search
            </Button>
          ) : (
            (newButton ?? undefined)
          )
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={query.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={query.isPending}
          />
        }
      />

      {creating ? (
        <NewGymDialog
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(next) => {
            setCreating(false);
            setCreated(next);
            setCursorStack([]);
            onFiltersChange(filtersForScope('all', {}));
          }}
        />
      ) : null}
    </div>
  );
}

/** An invited owner's badge, or "email unconfirmed" for one who signed up before invites and never confirmed. */
function OwnerBadge({ owner }: { readonly owner: AdminGymListItem['owner'] }) {
  const invite = ownerInviteBadge(owner);
  if (invite !== null) return <BadgeCell label={invite.label} tone={invite.tone} />;
  return owner.emailConfirmed ? null : <BadgeCell label="email unconfirmed" tone="mute" />;
}
